import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface PointillizeParams {
  dotSize: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Pointillize: Filter<PointillizeParams> = {
  id: "pointillize",
  displayName: "Pointillize",
  defaultParams: { dotSize: 6 },

  apply(input: PixelBuffer, params: PointillizeParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    dst.fill(255);

    if (w === 0 || h === 0 || params.dotSize < 1) { dst.set(src); return out; }

    const size = Math.round(params.dotSize);

    for (let cy = 0; cy < h; cy += size) {
      for (let cx = 0; cx < w; cx += size) {
        let sumR = 0, sumG = 0, sumB = 0, count = 0;
        const endY = Math.min(cy + size, h);
        const endX = Math.min(cx + size, w);

        for (let y = cy; y < endY; y++) {
          for (let x = cx; x < endX; x++) {
            const si = (y * w + x) * 4;
            sumR += src[si]!; sumG += src[si + 1]!; sumB += src[si + 2]!;
            count++;
          }
        }

        const avgR = Math.round(sumR / count);
        const avgG = Math.round(sumG / count);
        const avgB = Math.round(sumB / count);

        const lum = avgR * 0.299 + avgG * 0.587 + avgB * 0.114;
        const radius = Math.max(1, Math.round(size / 2 * (lum / 255)));
        const dotCx = cx + size / 2;
        const dotCy = cy + size / 2;

        for (let y = Math.max(0, dotCy - radius); y < Math.min(h, dotCy + radius); y++) {
          for (let x = Math.max(0, dotCx - radius); x < Math.min(w, dotCx + radius); x++) {
            const dx = x - dotCx, dy = y - dotCy;
            if (dx * dx + dy * dy <= radius * radius) {
              const o = (y * w + x) * 4;
              dst[o] = avgR; dst[o + 1] = avgG; dst[o + 2] = avgB; dst[o + 3] = 255;
            }
          }
        }
      }
    }
    return out;
  },
};
